import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';

import { startWith } from 'rxjs';

import { AbilityGuardDirective } from '@core/abilities-legacy';
import { Ability } from '@core/abilities-types';
import { FnBindPipe } from '@core/common-legacy';
import type { DisqualifyReason } from '@core/disqualify-reasons-types';
import { TranslatePipe } from '@core/i18n-legacy';
import type {
  ConnectedOverlayDirective,
  OverlayOriginDirective,
} from '@core/user-interface-legacy';
import {
  DropdownHeaderComponent,
  DropdownItemComponent,
  FormFieldComponent,
  IconButtonComponent,
  IconComponent,
  InputComponent,
  RtTooltip,
  ScrollableDirective,
} from '@core/user-interface-legacy';
import { includesWithTrim } from '@core/utils-legacy';
import { WalkthroughStepOriginDirective } from '@core/walkthrough-ui';

@Component({
  selector: 'rt-disqualify-reasons-dropdown-template',
  templateUrl: './disqualify-reasons-dropdown-template.component.html',
  styleUrls: ['./disqualify-reasons-dropdown-template.component.less'],
  imports: [
    TranslatePipe,
    DropdownHeaderComponent,
    FormFieldComponent,
    InputComponent,
    ReactiveFormsModule,
    DropdownItemComponent,
    IconComponent,
    ScrollableDirective,
    RtTooltip,
    WalkthroughStepOriginDirective,
    IconButtonComponent,
    FnBindPipe,
    AbilityGuardDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DisqualifyReasonsDropdownTemplateComponent {
  public readonly manageDisqualifyReasons = Ability.ManageDisqualifyReasons;

  public collection = input.required<DisqualifyReason[]>();
  public overlay = input.required<ConnectedOverlayDirective>();
  public origin = input<OverlayOriginDirective>();
  public hasClear = input(false, { transform: booleanAttribute });
  public hasEditOption = input(true, { transform: booleanAttribute });
  public hasBackAction = input(false, { transform: booleanAttribute });
  public stayBelowOrigin = input(false, { transform: booleanAttribute });

  public select = output<{
    disqualifyReason: DisqualifyReason;
  }>();
  public edit = output<void>();
  public clear = output<void>();
  public actionsDetails = output<{
    disqualifyReason: DisqualifyReason;
  }>();
  public goBack = output<void>();

  public dropdownItems = computed<DisqualifyReason[]>(() =>
    this.query()
      ? this.collection().filter(reason => includesWithTrim(reason.name, this.query()))
      : [...this.collection()],
  );
  public searchInputControl = new UntypedFormControl('');
  public query = toSignal(this.searchInputControl.valueChanges.pipe(startWith('')));

  public hasAutomatedActions(item: DisqualifyReason): boolean {
    return !!item?.actionTemplates?.length;
  }

  public resetSearch() {
    this.searchInputControl.setValue('');
  }

  public afterWalkthroughTooltipDispose() {
    this.edit.emit();
  }
}
